export const GOOGLE_CALENDAR_EVENT_CREATE_KEY = 'google-calendar-event-create';
export const GOOGLE_DOC_CREATE_KEY = 'google-doc-create';
export const SLACK_SEND_CHANNEL_MESSAGE_KEY = 'slack-send-channel-message';
export const GITHUB_PULL_REQUEST_COMMENT_KEY = 'github-pull-request-comment';
export const SHAREPOINT_DOCUMENT_CREATE_KEY = 'sharepoint-document-create';

export type DYNAMIC_ACTION_KEYS =
	| typeof GOOGLE_CALENDAR_EVENT_CREATE_KEY
	| typeof GOOGLE_DOC_CREATE_KEY
	| typeof SLACK_SEND_CHANNEL_MESSAGE_KEY
	| typeof GITHUB_PULL_REQUEST_COMMENT_KEY
	| typeof SHAREPOINT_DOCUMENT_CREATE_KEY;

// TODO Use actionId from BE, remove this
// https://product-fabric.atlassian.net/browse/CONVOAI-117

export const actionKeyToActionId: Record<
	DYNAMIC_ACTION_KEYS,
	{
		stg: string;
		prod: string;
	}
> = {
	'google-calendar-event-create': {
		stg: '57a5a90f-ab9c-4f1c-b661-3267fb23b5f2',
		prod: '57a5a90f-ab9c-4f1c-b661-3267fb23b5f2',
	},
	'google-doc-create': {
		stg: 'ari:cloud:ecosystem::extension/2357cbb7-bf5c-4fe4-a9c6-c8e172ccb5b1/3b53863d-a6bb-4818-91da-4de04f975f48/static/create-gdrive-document',
		prod: 'ari:cloud:ecosystem::extension/ff32b4a9-0056-4c40-bb6e-eebf3b1d82f7/c50f3d56-68fc-4c3c-8191-6717f173dc54/static/create-gdrive-document',
	},
	'slack-send-channel-message': {
		stg: '698c49a4-8c14-448d-a7c6-766298a5f22e',
		prod: '698c49a4-8c14-448d-a7c6-766298a5f22e',
	},
	'github-pull-request-comment': {
		stg: '88344c12-d3a8-4f9b-85ca-9c905de23570',
		prod: '88344c12-d3a8-4f9b-85ca-9c905de23570',
	},
	'sharepoint-document-create': {
		stg: 'c910d167-3796-4b72-b144-0a9a0c4ef8cc',
		prod: 'c910d167-3796-4b72-b144-0a9a0c4ef8cc',
	},
};

export const isDynamicAction = (actionKey: string): actionKey is DYNAMIC_ACTION_KEYS => {
	return actionKey in actionKeyToActionId;
};

export const mapActionToProduct = (actionKey: DYNAMIC_ACTION_KEYS): string => {
	switch (actionKey) {
		case GOOGLE_CALENDAR_EVENT_CREATE_KEY:
			return 'google-calendar';
		case GOOGLE_DOC_CREATE_KEY:
			return 'google-doc';
		case SLACK_SEND_CHANNEL_MESSAGE_KEY:
			return 'slack';
		case GITHUB_PULL_REQUEST_COMMENT_KEY:
			return 'github';
		case SHAREPOINT_DOCUMENT_CREATE_KEY:
			return 'sharepoint';
		default:
			return '';
	}
};

export const ActionKeys = {
	// Atlassian Projects
	CreateProjectProgressUpdate: 'atlassian-project-atlassian-atlassian-project-create-progress-up',
	// Jira
	UpdateWorkItemFields: 'jira-atlassian-work-item-update-single-field',
	UpdateWorkItemAllFieldTypes: 'jira-atlassian-work-item-update-all-field-types-bulk',
	JiraFindIssues: 'jira-find-issues',
	JiraSmartIssueCreateAllField: 'jira-atlassian-work-item-create-all-field-types',
	JiraIssueTransition: 'jira-issue-transition-bulk',
	JiraEpicsSuggest: 'jira-epics-suggest',
	JiraIssueDelete: 'jira-issue-delete',
	JiraIssueMove: 'jira-issue-move',
	JiraIssueAssign: 'jira-issue-assign-bulk',
	JiraVersionCreate: 'jira-version-create',
	JiraWorkflowEditor: 'jira-workflow-operations',
	// Confluence
	ConfluencePageCreate: 'confluence-page-create',
	ConfluencePageComment: 'confluence-page-comment',
	ConfluencePageAddLabel: 'confluence-label-create',
	// JSM
	JsmCreateRequest: 'jsm-create-request',
	JsmCreateRequestV2: 'jsm-create-request-v2',
	JsmCreateEmbeddedRequest: 'help-form-submit',
	JsmCreateSurvey: 'jsm-create-survey',
	JsmLivechat: 'jsm-livechat',
	JsmSolutionComposerPlanSummary: 'jsm-solution-composer-plan-summary',
	// Deep Research
	DeepResearchCreatePageAction: 'deep-research-report-confluence-export',
	// Studio
	StudioTriggerSolutionPlan: 'studio-trigger-solution-plan',
	StudioUpdateAgentDraft: 'studio-atlassian-studio-update-agent-draft',
	StudioWorkspaceUpdateAgentDraft: 'studio-atlassian-workspace-update-draft-agent',
	StudioAutomationTriggerRuleBuild: 'studio-automation-trigger-rule-build',
	UpdateAgentConfigurationTool: 'UpdateAgentConfigurationTool',
	StudioLandingPageRedirect: 'studio-landing-page-redirect',
	// Plans
	JiraPlansUpdateWorkSource: 'jira-atlassian-plan-update-work-sources',
	JiraPlansCreateProgramBoard: 'jira-plan-add-program-board-plugin',
	JiraPlansDeleteProgramBoard: 'Jira-Plan-Delete-Program-Board-Plugin',
	JiraPlansCreateWorkItem: 'jira-atlassian-plan-create-work-item',
	JiraPlansCreateRelease: 'Jira-Plan-Create-Release-Plugin',
	JiraPlansManageFeatures: 'jira-atlassian-plan-set-release-enabled',
	JiraPlansAddDependency: 'jira-atlassian-plan-add-work-item-dependencies',
	JiraPlansRemoveDependency: 'jira-atlassian-plan-remove-work-item-dependencies',
	// Capacity Planning
	ManageCapacityPlan: 'capacity-plan-update-single-action',
	// Atlassian Teams
	AtlassianTeamUpdateArchive: 'atlassian-team-atlassian-team-update-archive',
	// Focus Asks
	FocusCreateAsk: 'passionfruit-atlassian-workspace-create-ask',
	// Bitbucket
	BitbucketPullRequestComment: 'bitbucket-pull-request-comment',
	// Others
	StoreRecommendationCard: 'store-recommendation-card',
	RequireInformationTool: 'RequireInformationTool',
	HiringManagerTool: 'HiringManagerTool',
	JsmSuggestAndAddFieldsToRequestType: 'SuggestAndAddFieldsToRequestType',
	JsmAtlassianProjectCreateJsmRequestTypeWithFields:
		'jsm-atlassian-project-create-jsm-request-type-with-fields',
	// Convo AI agent tools
	ExecutePython: 'execute_python',
	PlanComplete: 'plan_complete',
	AskQuestion: 'ask_question',
	JsmAtlassianProjectCreateJsmQueue: 'jsm-atlassian-project-create-jsm-queue',
	JsmAtlassianProjectUpdateJsmQueue: 'jsm-atlassian-project-update-jsm-queue',
	JsmCreateSlaPlugin: 'jsm-create-sla-plugin',
	JsmUpdateSlaPlugin: 'jsm-update-sla-plugin',
	// Confluence AIFC
	AifcAdfUpdate: 'aifc-adf-update',
	// Add other keys here as needed
	ScheduledChat: 'ScheduleRovoChatTool',
	MultipleChoiceQuestionsTool: 'MultipleChoiceQuestionsTool',
	AgenticPlanTool: 'AgenticPlanTool',
} as const;

export const FOCUS_CREATE_ASK_KEY: (typeof ActionKeys)['FocusCreateAsk'] =
	ActionKeys.FocusCreateAsk;

export const EXECUTE_PYTHON_KEY = 'execute_python';
export const PLAN_COMPLETE_KEY = 'plan_complete';
export const ASK_QUESTION_KEY = 'ask_question';
// Actions can declare this when their UI replaces or supersedes the footer (e.g. user-answer cards).
export const ACTIONS_HIDING_FOOTER_BUTTONS: ReadonlySet<string> = new Set([ASK_QUESTION_KEY]);

export const ActionPredicateKeys = {
	DynamicAction: 'dynamicAction',
	McpTool: 'mcpTool',
} as const;

export const ACTION_RENDER_KEYS = Object.values(ActionKeys) as readonly string[];

export const GENERIC_EXTERNAL_ACTION_ERROR_KEY = 'generic-external-action-error';
export const JIRA_FIND_ISSUES_KEY = 'jira-find-issues';
export const JIRA_SMART_ISSUE_CREATE_ALL_FIELDS_KEY =
	'jira-atlassian-work-item-create-all-field-types';
export const JSM_CREATE_REQUEST_KEY = 'jsm-create-request';
export const JSM_CREATE_REQUEST_V2_KEY = 'jsm-create-request-v2';
export const JSM_CREATE_EMBEDDED_REQUEST_KEY = 'help-form-submit';
export const JSM_LIVECHAT_KEY = 'jsm-livechat';
export const JIRA_ISSUE_TRANSITION_KEY = 'jira-issue-transition-bulk';
export const JIRA_EPICS_SUGGEST_KEY = 'jira-epics-suggest';
export const JIRA_ISSUE_DELETE_KEY = 'jira-issue-delete';
export const JIRA_ISSUE_MOVE_KEY = 'jira-issue-move';
export const JIRA_ISSUE_ASSIGN_KEY = 'jira-issue-assign-bulk';
export const JIRA_VERSION_CREATE_KEY = 'jira-version-create';
export const MANAGE_CAPACITY_PLAN_KEY = 'capacity-plan-update-single-action';
export const JIRA_WORKFLOW_EDITOR = 'jira-workflow-operations';

export const CONFLUENCE_PAGE_CREATE_KEY = 'confluence-page-create';
export const CONFLUENCE_PAGE_COMMENT_KEY = 'confluence-page-comment';
export const CONFLUENCE_PAGE_RENAME_KEY = 'confluence-page-rename';
export const CONFLUENCE_PAGE_ADD_LABEL_KEY = 'confluence-label-create';
export const CONFLUENCE_PAGE_MOVE_KEY = 'confluence-page-move';

export const DEEP_RESEARCH_CREATE_PAGE_ACTION_KEY = 'deep-research-report-confluence-export';

export const REQUIRE_INFORMATION_TOOL = 'RequireInformationTool';
export const HIRING_MANAGER_TOOL_KEY = 'HiringManagerTool';

export const STUDIO_TRIGGER_SOLUTION_PLAN_KEY = 'studio-trigger-solution-plan';
export const STUDIO_ATLASSIAN_STUDIO_UPDATE_AGENT_DRAFT_KEY =
	'studio-atlassian-studio-update-agent-draft';
export const STUDIO_ATLASSIAN_WORKSPACE_UPDATE_DRAFT_AGENT_KEY =
	'studio-atlassian-workspace-update-draft-agent';

export const UPDATE_AGENT_CONFIGURATION_TOOL_KEY = 'UpdateAgentConfigurationTool';
export const JSM_JOURNEY_BUILDER_KEY = 'jsm-invoke-orchestrator-agent';
export const JSM_CREATE_SURVEY_KEY = 'jsm-atlassian-project-create-jsm-survey';
export const JSM_QUEUE_CREATE_KEY = 'jsm-atlassian-project-create-jsm-queue';
export const JSM_QUEUE_UPDATE_KEY = 'jsm-atlassian-project-update-jsm-queue';
export const JSM_SLA_CREATE_KEY = 'jsm-create-sla-plugin';
export const JSM_SLA_UPDATE_KEY = 'jsm-update-sla-plugin';
export const JSM_REQUEST_TYPE_UPDATED_KEY =
	'jsm-atlassian-project-create-jsm-request-type-with-fields';
export const JSM_SUGGEST_AND_ADD_FIELDS_TO_REQUEST_TYPE_KEY = 'SuggestAndAddFieldsToRequestType';
export const JSM_CREATE_KB_TOPICS_KEY = 'jsm-atlassian-project-create-jsm-kb-topics';
export const JSM_CREATE_KB_ARTICLES_KEY = 'jsm-atlassian-project-create-jsm-kb-articles';

export const STUDIO_AUTOMATION_TRIGGER_RULE_BUILD_KEY = 'studio-automation-trigger-rule-build';

export const JIRA_PLANS_UPDATE_WORK_SOURCE_KEY = 'jira-atlassian-plan-update-work-sources';
export const JIRA_PLANS_ADD_PROGRAM_BOARD_KEY = 'jira-plan-add-program-board-plugin';
export const JIRA_PLANS_DELETE_PROGRAM_BOARD_KEY = 'Jira-Plan-Delete-Program-Board-Plugin';
export const JIRA_PLANS_CREATE_WORK_ITEM_KEY = 'jira-atlassian-plan-create-work-item';
export const JIRA_PLANS_MANAGE_FEATURES_KEY = 'jira-atlassian-plan-set-release-enabled';
export const JIRA_PLANS_ADD_DEPENDENCY_KEY = 'jira-atlassian-plan-add-work-item-dependencies';
export const JIRA_PLANS_REMOVE_DEPENDENCY_KEY = 'jira-atlassian-plan-remove-work-item-dependencies';
export const JIRA_PLANS_CREATE_RELEASE_KEY = 'Jira-Plan-Create-Release-Plugin';
export const STORE_RECOMMENDATION_ACTION_KEY = 'store-recommendation-card';
export const JIRA_HAM_AI_INSIGHTS_PREVIEW_KEY = 'jira-ham-ai-insights-preview';

export const ATLASSIAN_TEAM_UPDATE_ARCHIVE_KEY = 'atlassian-team-atlassian-team-update-archive';

export const SCHEDULED_CHAT_KEY = 'ScheduleRovoChatTool';

export const MULTIPLE_CHOICE_QUESTIONS_TOOL_KEY = 'MultipleChoiceQuestionsTool';

export const AGENTIC_PLAN_TOOL_KEY = 'AgenticPlanTool';

// used for testing only
export const customMessageActionKeys: string[] = [
	JIRA_FIND_ISSUES_KEY,
	JIRA_EPICS_SUGGEST_KEY,
	JIRA_ISSUE_DELETE_KEY,
	JIRA_ISSUE_MOVE_KEY,
];

export type ACTION_KEYS_WITH_CUSTOM_UI_TYPE =
	| DYNAMIC_ACTION_KEYS
	| (typeof ActionKeys)[keyof typeof ActionKeys]
	| typeof JIRA_VERSION_CREATE_KEY
	| typeof JSM_CREATE_REQUEST_KEY
	| typeof JSM_CREATE_EMBEDDED_REQUEST_KEY
	| typeof JIRA_ISSUE_TRANSITION_KEY
	| typeof JIRA_EPICS_SUGGEST_KEY
	| typeof JIRA_ISSUE_DELETE_KEY
	| typeof JIRA_ISSUE_MOVE_KEY
	| typeof JIRA_ISSUE_ASSIGN_KEY
	| typeof CONFLUENCE_PAGE_CREATE_KEY
	| typeof CONFLUENCE_PAGE_COMMENT_KEY
	| typeof JIRA_WORKFLOW_EDITOR
	| typeof DEEP_RESEARCH_CREATE_PAGE_ACTION_KEY
	| typeof REQUIRE_INFORMATION_TOOL
	| typeof GENERIC_EXTERNAL_ACTION_ERROR_KEY
	| typeof JSM_JOURNEY_BUILDER_KEY
	| typeof JSM_CREATE_SURVEY_KEY
	| typeof JIRA_PLANS_UPDATE_WORK_SOURCE_KEY
	| typeof STORE_RECOMMENDATION_ACTION_KEY
	| typeof SCHEDULED_CHAT_KEY
	| typeof FOCUS_CREATE_ASK_KEY
	| typeof MULTIPLE_CHOICE_QUESTIONS_TOOL_KEY
	| typeof AGENTIC_PLAN_TOOL_KEY;
